import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherCalculatorSection from "../../../components/OtherCalculatorSection";
import AISkeleton from "../../../components/skeleton/AISkeleton";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateIBWMutation,
  useGetIBWQuery,
  useUpdateIBWMutation,
} from "../../../redux/Apis/Calculator/IBW";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion } from "../../../utils/helper";

const IdealBodyWeightCalculator = () => {
  const { t } = useTranslate("ibw");
  const lang = useSelector((state) => state.language.lang);
  const { user } = useSelector((state) => state.authSlice);
  const { CALCULATORS, CALCULATOR_DATA } = useStaticData();
  const location = useLocation();
  const navigate = useNavigate();
  const [gender, setGender] = useState("male");
  const [height, setHeight] = useState("");
  const [heightUnit, setHeightUnit] = useState("cm");
  const [frameSize, setFrameSize] = useState("medium");
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const [createIBW] = useCreateIBWMutation();
  const [updateIBW] = useUpdateIBWMutation();
  const {
    data: ibwHistoryData,
    refetch: refetchIBW,
    isLoading,
  } = useGetIBWQuery({ lang }, { skip: !user });
  const courseState = location?.state || {};
  // const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  const {
    data: ibwData,
    refetch: refetchIbwData,
    isLoading: loadingIbw,
  } = useGetIBWQuery(
    { startdate: startDate, enddate: endDate, lang },
    { skip: !startDate || !endDate || !user },
  );

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchIBW();
    }
  }, [user, refetchIBW]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchIbwData();
    }
  }, [startDate, endDate, user, refetchIbwData]);

  // useEffect(() => {
  //   setMetaTags(
  //     "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
  //     "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
  //   );
  // }, []);

  useEffect(() => {
    if (ibwHistoryData) {
      setHistoryData(ibwHistoryData.data || []);
    }
  }, [ibwHistoryData]);

  const calculateIBW = () => {
    if (!height || isNaN(height) || height <= 0) {
      toast.error(t("message.invalidHeight"));
      return;
    }

    const heightInCm =
      heightUnit === "cm" ? parseFloat(height) : parseFloat(height) * 2.54;

    const heightInInches = heightInCm / 2.54;

    const heightOver = heightInInches - 60;

    const frameFactor =
      frameSize === "small" ? 0.9 : frameSize === "large" ? 1.1 : 1;

    const robinson =
      gender === "male"
        ? (52 + 1.9 * heightOver) * frameFactor
        : (49 + 1.7 * heightOver) * frameFactor;

    const miller =
      gender === "male"
        ? (56.2 + 1.41 * heightOver) * frameFactor
        : (53.1 + 1.36 * heightOver) * frameFactor;

    const hamwi =
      gender === "male"
        ? (48 + 2.7 * heightOver) * frameFactor
        : (45.5 + 2.2 * heightOver) * frameFactor;

    const ibwRange = {
      robinson: robinson.toFixed(1),
      miller: miller.toFixed(1),
      hamwi: hamwi.toFixed(1),
      min: Math.min(robinson, miller, hamwi).toFixed(1),
      max: Math.max(robinson, miller, hamwi).toFixed(1),
    };

    setResult(ibwRange);
    toast.success(t("message.calculateSuccess"));
    return ibwRange;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("message.notCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result} kg</p>
      </div>
    );
  };

  const generateAICoach = async (inputResult) => {
    try {
      // if (!courseId || !sectionId) return;

      setAiLoading(true);

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "Ideal Body Weight",
        result: [
          {
            gender: inputResult.gender,
            height: inputResult.height,
            frameSize: inputResult.size,
            result_range: {
              robinson: inputResult.result_range.robinson,
              miller: inputResult.result_range.miller,
              hamwi: inputResult.result_range.hamwi,
            },
            result: `${inputResult.result}`,
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveResult = async (type, result) => {
    if (!result) {
      toast.error(t("message.saveBefore"));
      return;
    }

    if (!user) {
      toast.error(t("message.loginFirst"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        gender,
        height: `${height} ${heightUnit}`,
        size: frameSize,
        result_range: {
          robinson: result.result_range.robinson,
          miller: result.result_range.miller,
          hamwi: result.result_range.hamwi,
        },
        result: `${result.result}`,
      };
      if (courseId) payload.course = courseId;
      const response = await createIBW({ payload, params: { lang } }).unwrap();
      toast.success(response.message || t("message.saveSuccess"));
      refetchIBW();
      refetchIbwData();
      // if (courseId && sectionId && templateId) {
      await generateAICoach(payload);
      // }
    } catch (error) {
      console.error("Error saving IBW:", error);
      toast.error(t("message.saveError"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        await updateIBW(payload).unwrap();
        toast.success(t("message.deleteSuccess"));
        refetchIBW();
        refetchIbwData();
      }
    } catch (error) {
      console.error("Error deleting IBW:", error);
      toast.error(t("message.deleteError"));
    }
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  if (isLoading || loadingIbw) {
    return <Loader loading={true} />;
  }

  return (
    <>
      <Helmet>
        <title>
          Ideal Body Weight Calculator | Healthy Weight Range | Traineego
        </title>

        <meta
          name="description"
          content="Estimate your ideal body weight based on your height and body composition goals using Traineego's free calculator."
        />

        <meta name="robots" content="index,follow" />
        <link
          rel="canonical"
          href="https://traineego.com/fitness-calculators/ideal-body-weight"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Ideal Body Weight Calculator",
            url: "https://traineego.com/fitness-calculators/ideal-body-weight",
            description:
              "Estimate your ideal body weight based on your height and body composition goals using Traineego's free calculator.",
            publisher: {
              "@type": "Organization",
              name: "Traineego",
              url: "https://traineego.com",
            },
          })}
        </script>
      </Helmet>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>
          <div className="row d-flex">
            <div className="col-md-12 mt-4 p-5 fitness-card">
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">
                    {t("form.calculateTitle")}
                  </h4>
                  <div className="mb-3">
                    <Input
                      label={t("form.genderLabel")}
                      type="select"
                      options={CALCULATOR_DATA.GENDER}
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <Input
                      label={t("form.frameSizeLabel")}
                      type="select"
                      options={CALCULATOR_DATA.FRAME_SIZE}
                      value={frameSize}
                      onChange={(e) => setFrameSize(e.target.value)}
                      required
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-8">
                      <Input
                        label={t("form.heightLabel")}
                        placeholder={t("form.heightPlaceholder")}
                        required
                        type="number"
                        value={height}
                        onChange={(e) => setHeight(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <Input
                        label={t("form.unitLabel")}
                        type="select"
                        options={CALCULATOR_DATA.HEIGHT_UNITS}
                        value={heightUnit}
                        onChange={(e) => setHeightUnit(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mt-5">
                    <div className="fitness-branded col-12 py-2 px-4 d-flex justify-content-between align-items-center">
                      <p className="grey-text m-0">
                        {t("form.calculatePrompt")}
                      </p>
                      <Button
                        text={t("form.calculateBtn")}
                        onClick={calculateIBW}
                      />
                    </div>
                  </div>
                  {location.state?.returnTo && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("form.continueLearningBtn")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-IBW`,
                              resultData: {
                                type: "IBW",
                                result: `${result.min} – ${result.max}`,
                                value: `Ideal Weight: ${result.min} – ${result.max} kg`,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>
                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    {result ? (
                      <>
                        <h6>
                          {t("result.rangeText")} {result.min} kg – {result.max}{" "}
                          kg
                        </h6>
                        <p className="p-sm">{t("result.formulaNote")}</p>
                        <div className="p-sm mt-3">
                          <div className="d-flex justify-content-between py-1 border-bottom">
                            <span>{t("result.robinson")}</span>
                            <span className="fw-bold">
                              {result.robinson} kg
                            </span>
                          </div>
                          <div className="d-flex justify-content-between py-1 border-bottom">
                            <span>{t("result.miller")}</span>
                            <span className="fw-bold">{result.miller} kg</span>
                          </div>
                          <div className="d-flex justify-content-between py-1">
                            <span>{t("result.hamwi")}</span>
                            <span className="fw-bold">{result.hamwi} kg</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <h6>{t("result.notCalculated")}</h6>
                    )}
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        height: "auto",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 text-black">{t("cta.title")}</p>
                      <p className="m-0 grey-text p-sm">{t("cta.subtitle")}</p>
                      <Button text={t("cta.button")} to="/courses-tracks" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <FitnessToolsResultCard
              type="IBW"
              result={
                result
                  ? {
                      result: `${result.min} – ${result.max}`,
                      result_range: {
                        robinson: result.robinson,
                        miller: result.miller,
                        hamwi: result.hamwi,
                      },
                      timestamp: new Date().toISOString(),
                    }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={ibwData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                  <img
                    src="https://traineego.com/assets/images/favicon-2026.ico"
                    alt="AI Coach"
                    style={{
                      width: "45px",
                      marginRight: "8px",
                      borderRadius: "25px",
                    }}
                  />
                  {t("aiSuggestion.title")}
                </h4>

                {showSkeleton ? (
                  // <p style={{ color: "#aaa" }}>
                  //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  // </p>
                  <AISkeleton />
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherCalculatorSection
        data={CALCULATORS.filter(
          (item) => item.route !== window.location.pathname,
        )}
      />
      <Footer />
    </>
  );
};

export default IdealBodyWeightCalculator;
